import React from 'react'
import {
  Box,
  Typography,
  Card,
} from '@mui/material'
import { TouchApp as TouchAppIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import Section from '../../../../components/Notebook/Section'

/**
 * 预览面板组件
 * 用户未点击时显示空状态；点击左侧大纲或章节后显示对应内容
 */
const PreviewPanel = ({
  notebookData,
  generationProgress,
  onConfirmGenerate,
  isUploading,
  selectedOutlineForPreview,
  selectedSectionForPreview,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const hasSelection = selectedOutlineForPreview || selectedSectionForPreview

  // 无选择时：不生成/渲染任何内容，仅显示空状态提示
  if (!hasSelection) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('uploadNotebook.preview') || '预览'}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <TouchAppIcon sx={{ fontSize: 48, mb: 2, color: 'action.disabled', opacity: 0.6 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
            {t('uploadNotebook.previewClickHint') || '点击左侧大纲或章节可在右侧预览'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('uploadNotebook.previewClickDesc') || '在步骤 2 点击大纲章节，或在步骤 5 点击笔记本章节，内容将显示在此'}
          </Typography>
        </Box>
      </Card>
    )
  }

  // 有选择时：仅显示选中的那一项内容
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('uploadNotebook.preview') || '预览'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {selectedOutlineForPreview && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedOutlineForPreview.order || ''}. {selectedOutlineForPreview.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedOutlineForPreview.description}
            </Typography>
            {(() => {
              const section = notebookData?.sections?.find(
                (s) => s.section_title === selectedOutlineForPreview.title
              )
              if (section?.section_title) {
                return <Section section={section} title={selectedOutlineForPreview.title} order={selectedOutlineForPreview.order} hideTitle />
              }
              return (
                <Box>
                  {selectedOutlineForPreview.source_content ? (
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('uploadNotebook.originalExcerpt') || '原文'}
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          overflow: 'auto',
                          maxHeight: 400,
                          fontSize: '0.8rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'inherit',
                          m: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {selectedOutlineForPreview.source_content}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('uploadNotebook.sectionNotGenerated') || '章节内容尚未生成'}
                    </Typography>
                  )}
                </Box>
              )
            })()}
          </Box>
        )}
        {selectedSectionForPreview && !selectedOutlineForPreview && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedSectionForPreview.outlineItem?.order || ''}. {selectedSectionForPreview.section?.section_title}
            </Typography>
            <Section
              section={selectedSectionForPreview.section}
              title={selectedSectionForPreview.section?.section_title}
              order={selectedSectionForPreview.outlineItem?.order}
              hideTitle
            />
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default PreviewPanel
